// Download flow for the optional engines.
import { fmt, formatBytes, formatPercent, t } from '../i18n';
import { type OptionalEngine, downloadSize, hasEngine, isEngineCached, prefetchEngine } from '../lib/engines/vendor';
import type { Progress } from '../tools/types';
import { confirmDialog } from '../ui/feedback';

export function engineName(id: OptionalEngine): string {
  const e = t().engines;
  return { ocr: e.ocrName, gs: e.gsName, mupdf: e.mupdfName, office: e.officeName }[id];
}

/**
 * Downloads above this size ask for consent first. Smaller engines download
 * straight away with a progress bar; the tool page states their size up front.
 */
const CONFIRM_ABOVE = 20 * 1024 * 1024;

/**
 * Makes sure an engine is available, downloading it on first use. Returns
 * false when the engine is not part of this build or the user declines.
 */
export async function ensureEngine(id: OptionalEngine, progress: Progress, signal?: AbortSignal): Promise<boolean> {
  if (!hasEngine(id)) return false;
  if (await isEngineCached(id)) return true;
  const d = t();
  const name = engineName(id);
  if (downloadSize(id) > CONFIRM_ABOVE) {
    const ok = await confirmDialog(d.engines.needDownloadTitle, fmt(d.engines.needDownloadText, { engine: name, size: formatBytes(downloadSize(id)) }), d.engines.download);
    if (!ok) return false;
  }
  await prefetchEngine(
    id,
    (loaded, total) => progress(total ? loaded / total : null, fmt(d.engines.downloading, { engine: name, percent: formatPercent(total ? loaded / total : 0) })),
    signal,
  );
  return true;
}
